
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

interface PricingCardProps {
  title: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  maxInstallations: number;
  features: string[];
}

const PricingCard = ({
  title,
  description,
  monthlyPrice,
  yearlyPrice,
  maxInstallations,
  features
}: PricingCardProps) => {
  const [isYearly, setIsYearly] = useState(false);
  const price = isYearly ? yearlyPrice : monthlyPrice;
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubscribe = async () => {
    try {
      // Navigate to subscribe page with plan details
      navigate('/subscribe', {
        state: {
          plan: {
            title,
            price,
            billingPeriod: isYearly ? 'yearly' : 'monthly',
            maxInstallations
          }
        }
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to initiate subscription. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className={cn(
      "w-full max-w-md rounded-lg border bg-card text-card-foreground shadow-sm",
      "hover:shadow-lg transition-shadow duration-200"
    )}>
      <div className="p-6">
        <h3 className="text-2xl font-bold">{title}</h3>
        <p className="mt-2 text-muted-foreground">{description}</p>
        <div className="mt-4 flex items-baseline">
          <span className="text-3xl font-bold">${price}</span>
          <span className="ml-1 text-muted-foreground">/{isYearly ? 'year' : 'month'}</span>
        </div>
        
        <div className="mt-4">
          <Button 
            variant="outline" 
            className="w-full" 
            onClick={() => setIsYearly(!isYearly)}
          >
            {isYearly ? 'Switch to Monthly' : 'Switch to Yearly'} 
            {isYearly && <span className="ml-1 text-sm text-muted-foreground">(Save 17%)</span>}
          </Button>
        </div>

        <Button 
          className="mt-6 w-full" 
          size="lg"
          onClick={handleSubscribe}
        >
          Subscribe Now
        </Button>

        <ul className="mt-6 space-y-4">
          {features.map((feature, index) => (
            <li key={index} className="flex">
              <Check className="h-5 w-5 text-green-500 shrink-0" />
              <span className="ml-3">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default PricingCard;
